import { useEffect, useMemo, useRef, useState } from 'react'
import { Award, Lock, Package, Plus, TrendingUp, Users, X, CreditCard } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { chipStyle } from '@/components/ui/palette'
import { AdminOnlyNotice } from '@/components/features/spaces/AdminOnlyNotice'
import { sdk } from '@/lib/sdk'
import { useAuth } from '@/lib/AuthContext'
import {
  emptyAccessGrants,
  type AccessGrants,
  type AccessLevel,
  type RequiredBySection
} from '@/types/access'

type Source = 'groups' | 'products' | 'subscriptionGroups' | 'badges' | 'level'

const SOURCES: Source[] = ['groups', 'products', 'subscriptionGroups', 'badges', 'level']
/* F-151a/F-151b: the badge catalog and the ladder are read and granted by owner and admin only. */
const ENGAGEMENT_SOURCES: Source[] = ['badges', 'level']

interface Option {
  id: string
  label: string
  color?: string | null
  imageUrl?: string | null
  visibility?: 'public' | 'private'
  /** F-151b: the level a `level` option stands for. */
  level?: AccessLevel
}

const SOURCE_ICON: Record<Source, typeof Users> = {
  groups: Users,
  products: Package,
  subscriptionGroups: CreditCard,
  badges: Award,
  level: TrendingUp
}

interface AccessGrantPickerProps {
  /** What this scope requires. The parent owns it; this only reports changes. */
  value: AccessGrants
  onChange: (value: AccessGrants) => void
  /** The section's requirement, drawn locked. Combines by AND, not as more items. */
  requiredBySection?: RequiredBySection | null
  /** Products wired from the products screen: real, and not editable here. */
  fromProducts?: AccessGrants['products']
  communityId?: string
  disabled?: boolean
  className?: string
}

/**
 * The right-hand column of the section and space modals.
 *
 * One component for all the sources, because the only thing that differs
 * between "a group", "a product" and "a subscription tier" is where the list
 * comes from — and three near-identical pickers is three places for the next
 * change to be applied twice and forgotten once.
 *
 * What the scope itself requires is editable. What it inherits is drawn with a
 * padlock and no ✕: the section is edited in the section's own modal, and the
 * products link in the products screen. A chip with an ✕ that silently comes
 * back is worse than a chip that says where it lives.
 */
export function AccessGrantPicker({
  value,
  onChange,
  requiredBySection,
  fromProducts,
  communityId,
  disabled,
  className
}: AccessGrantPickerProps) {
  const { t } = useTranslation(['common'])
  /* F-151a: the badge catalog (and a private badge's name) is owner and admin only. Anyone else
     sees the public badges and cannot add one; the private ones stay in `value`, so saving keeps them. */
  const { hasRole } = useAuth()
  const managesBadges = hasRole(['owner', 'admin'])
  const sources = managesBadges ? SOURCES : SOURCES.filter((source) => !ENGAGEMENT_SOURCES.includes(source))
  const shownBadge = (badge: { visibility?: 'public' | 'private' }) => managesBadges || badge.visibility === 'public'

  const [options, setOptions] = useState<Record<Source, Option[]>>({
    groups: [],
    products: [],
    subscriptionGroups: [],
    badges: [],
    level: []
  })
  /* F-151b: with the gamification off a level opens nothing (decision 2); `null` while unknown. */
  const [gamificationOn, setGamificationOn] = useState<boolean | null>(null)
  /* `limited`: the plans list is the public one (the viewer may not read the
     management list); `failed`: no list at all. Either way the dropdown is
     incomplete, and an empty one with no reason reads as "no plans exist". */
  const [plansNotice, setPlansNotice] = useState<'limited' | 'failed' | null>(null)
  const [openSource, setOpenSource] = useState<Source | null>(null)
  const [search, setSearch] = useState('')
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false

    /* The management list needs owner, admin or finance (SEC-54). A moderator
       who edits a space gets 403 there; the checkout list is the one they can
       read, but it leaves out the link-only plans, so say that instead of
       showing a list that looks complete. */
    const loadPlans = async (): Promise<{ list: unknown; notice: 'limited' | 'failed' | null }> => {
      try {
        return { list: await sdk.subscriptionGroups.list(), notice: null }
      } catch (error) {
        if ((error as { response?: { status?: number } })?.response?.status !== 403) return { list: [], notice: 'failed' }
      }
      try {
        return { list: await sdk.checkout.getSubscriptionGroups(), notice: 'limited' }
      } catch {
        return { list: [], notice: 'failed' }
      }
    }

    const load = async () => {
      /* Each of the three can fail on its own — a community with no products
         should still be able to grant a group — so nothing here is allowed to
         take the others down with it. */
      /* F-151a: the badge catalog is owner and admin only; anyone else gets an empty list. */
      /* F-151b: the ladder (its first read stores the default levels, so each has an id) and whether the gamification is on. */
      const [groups, products, subscriptionGroups, badges, ladder, config] = await Promise.all([
        sdk.memberGroups.list().catch(() => []),
        communityId ? sdk.products.list().catch(() => []) : Promise.resolve([]),
        communityId ? loadPlans() : Promise.resolve({ list: [], notice: null as 'limited' | 'failed' | null }),
        communityId && managesBadges ? sdk.badges.list().catch(() => []) : Promise.resolve([]),
        communityId && managesBadges ? sdk.gamification.getLevels().catch(() => null) : Promise.resolve(null),
        communityId && managesBadges ? sdk.gamification.getConfig().catch(() => null) : Promise.resolve(null)
      ])

      if (cancelled) return
      setPlansNotice(subscriptionGroups.notice)
      setGamificationOn(config ? config.isEnabled !== false : null)

      /* The lists come as an array, `{ items }` or a leftover `{ data }`; each row carries what its option draws. */
      type Row = { id: string; name?: string; title?: string; color?: string | null }
      const items = (result: unknown): Row[] => {
        if (Array.isArray(result)) return result as Row[]
        const wrapped = result as { items?: Row[]; data?: Row[] } | null
        return wrapped?.items ?? wrapped?.data ?? []
      }

      setOptions({
        groups: items(groups).map((group) => ({
          id: group.id, label: group.name ?? '', color: group.color
        })),
        products: items(products).map((product) => ({
          id: product.id, label: product.title ?? ''
        })),
        subscriptionGroups: items(subscriptionGroups.list).map((group) => ({
          id: group.id, label: group.name ?? ''
        })),
        badges: badges.map((badge) => ({
          id: badge.id, label: badge.name, imageUrl: badge.imageUrl, visibility: badge.visibility
        })),
        level: (ladder?.levels ?? []).map((level) => ({
          id: level.id ?? `n${level.level}`,
          label: t('common:access.levelOption', { level: level.level, name: level.name }),
          level: { id: level.id, level: level.level, name: level.name, minPoints: level.minPoints, imageUrl: level.imageUrl }
        }))
      })
    }

    void load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` only labels the options
  }, [communityId, managesBadges])

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setOpenSource(null)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const chosen = value ?? emptyAccessGrants()
  const chosenBadges = chosen.badges ?? []

  const chosenIds = useMemo(() => ({
    groups: new Set(chosen.groups.map((item) => item.id)),
    products: new Set(chosen.products.map((item) => item.id)),
    subscriptionGroups: new Set(chosen.subscriptionGroups.map((item) => item.id)),
    badges: new Set((chosen.badges ?? []).map((item) => item.id)),
    level: new Set(chosen.minLevel ? [chosen.minLevel.id ?? `n${chosen.minLevel.level}`] : [])
  }), [chosen])

  const add = (source: Source, option: Option) => {
    if (chosenIds[source].has(option.id)) return

    const next: AccessGrants = {
      groups: [...chosen.groups],
      products: [...chosen.products],
      subscriptionGroups: [...chosen.subscriptionGroups],
      badges: [...chosenBadges],
      minLevel: chosen.minLevel
    }

    if (source === 'products') next.products.push({ id: option.id, title: option.label })
    else if (source === 'groups') next.groups.push({ id: option.id, name: option.label, color: option.color })
    /* One level per scope (from that level up): choosing another replaces it. */
    else if (source === 'level') next.minLevel = option.level ?? null
    else if (source === 'badges') next.badges!.push({ id: option.id, name: option.label, imageUrl: option.imageUrl, visibility: option.visibility })
    else next.subscriptionGroups.push({ id: option.id, name: option.label })

    onChange(next)
    setOpenSource(null)
    setSearch('')
  }

  const remove = (source: Source, id: string) => {
    onChange({
      groups: source === 'groups' ? chosen.groups.filter((item) => item.id !== id) : chosen.groups,
      products: source === 'products' ? chosen.products.filter((item) => item.id !== id) : chosen.products,
      subscriptionGroups: source === 'subscriptionGroups'
        ? chosen.subscriptionGroups.filter((item) => item.id !== id)
        : chosen.subscriptionGroups,
      badges: source === 'badges' ? chosenBadges.filter((item) => item.id !== id) : chosenBadges,
      minLevel: source === 'level' ? null : chosen.minLevel
    })
  }

  const visibleOptions = (source: Source) => {
    const term = search.trim().toLowerCase()
    return options[source]
      .filter((option) => !chosenIds[source].has(option.id))
      .filter((option) => !term || option.label.toLowerCase().includes(term))
  }

  const chip = (
    key: string,
    label: string,
    icon: typeof Users,
    { color, onRemove, note }: { color?: string | null; onRemove?: () => void; note?: string } = {}
  ) => {
    const Icon = icon
    return (
      <li
        key={key}
        className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm"
        style={color !== undefined ? chipStyle(color, label) : undefined}
        title={note}
      >
        <Icon className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            aria-label={t('common:access.remove')}
            className="flex-shrink-0 rounded p-0.5 hover:bg-black/10 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <Lock className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
        )}
      </li>
    )
  }

  const inheritedCount = requiredBySection
    ? requiredBySection.groups.length
      + requiredBySection.products.length
      + requiredBySection.subscriptionGroups.length
      + (requiredBySection.badges ?? []).filter(shownBadge).length
      + (requiredBySection.minLevel ? 1 : 0)
    : 0

  const ownCount = chosen.groups.length + chosen.products.length + chosen.subscriptionGroups.length + chosenBadges.length
    + (chosen.minLevel ? 1 : 0)
  const levelLabel = (level: AccessLevel) => t('common:access.levelChip', { level: level.level, name: level.name })
  /* A private badge opens it too, but the refusal never names it: say so next to the chip. */
  const badgeNote = (badge: { visibility?: 'public' | 'private' }) =>
    badge.visibility === 'private' ? t('common:access.privateBadgeNote') : undefined
  const badgeLabel = (badge: { name: string; visibility?: 'public' | 'private' }) =>
    badge.visibility === 'private' ? `${badge.name} (${t('common:access.privateBadgeTag')})` : badge.name
  const productCount = fromProducts?.length ?? 0

  return (
    <div className={cn('flex min-h-0 flex-col gap-4', className)} data-access-picker>
      {requiredBySection && inheritedCount > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">{t('common:access.requiredBySection')}</p>
          <p className="text-xs text-gray-500">
            {t('common:access.requiredBySectionHint', { section: requiredBySection.sectionTitle })}
          </p>
          <ul className="space-y-1.5">
            {requiredBySection.groups.map((group) =>
              chip(`s-g-${group.id}`, group.name, Users, { color: group.color }))}
            {requiredBySection.products.map((product) =>
              chip(`s-p-${product.id}`, product.title, Package))}
            {requiredBySection.subscriptionGroups.map((group) =>
              chip(`s-s-${group.id}`, group.name, CreditCard))}
            {(requiredBySection.badges ?? []).filter(shownBadge).map((badge) =>
              chip(`s-b-${badge.id}`, badgeLabel(badge), Award, { note: badgeNote(badge) }))}
            {requiredBySection.minLevel && chip('s-l', levelLabel(requiredBySection.minLevel), TrendingUp)}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-sm font-medium">
          {requiredBySection && inheritedCount > 0
            ? t('common:access.alsoRequires')
            : t('common:access.title')}
        </p>

        <div className="relative flex flex-wrap gap-1.5" ref={popoverRef}>
          {sources.map((source) => {
            const Icon = SOURCE_ICON[source]
            return (
              <button
                key={source}
                type="button"
                disabled={disabled}
                onClick={() => {
                  setOpenSource(openSource === source ? null : source)
                  setSearch('')
                }}
                className={cn(
                  'flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors',
                  openSource === source ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
              >
                <Plus className="h-3 w-3" />
                <Icon className="h-3 w-3" />
                <span>{t(source === 'level' ? 'common:access.levelSource' : `common:access.${source}`)}</span>
              </button>
            )
          })}

          {openSource && (
            <div className="absolute left-0 top-full z-50 mt-1 w-full min-w-56 rounded-md border bg-white shadow-lg">
              <input
                autoFocus
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('common:access.searchPlaceholder')}
                className="w-full border-b px-2 py-1.5 text-sm outline-none"
              />
              <ul className="max-h-48 overflow-y-auto py-1">
                {visibleOptions(openSource).map((option) => (
                  <li key={option.id}>
                    <button
                      type="button"
                      onClick={() => add(openSource, option)}
                      className="w-full truncate px-2 py-1.5 text-left text-sm hover:bg-gray-100"
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
                {visibleOptions(openSource).length === 0 && (
                  <li className="px-2 py-1.5 text-sm text-gray-500">{t('common:access.noResults')}</li>
                )}
              </ul>
            </div>
          )}
        </div>

        {plansNotice && (
          <p role="status" className="text-xs text-amber-600">
            {t(`common:access.plansNotice.${plansNotice}`)}
          </p>
        )}

        <ul className="space-y-1.5">
          {chosen.groups.map((group) =>
            chip(`g-${group.id}`, group.name, Users, {
              color: group.color, onRemove: () => remove('groups', group.id)
            }))}
          {chosen.products.map((product) =>
            chip(`p-${product.id}`, product.title, Package, {
              onRemove: () => remove('products', product.id)
            }))}
          {chosen.subscriptionGroups.map((group) =>
            chip(`s-${group.id}`, group.name, CreditCard, {
              onRemove: () => remove('subscriptionGroups', group.id)
            }))}
          {chosenBadges.filter(shownBadge).map((badge) =>
            chip(`b-${badge.id}`, badgeLabel(badge), Award, {
              note: badgeNote(badge),
              onRemove: () => remove('badges', badge.id)
            }))}
          {chosen.minLevel && chip('l', levelLabel(chosen.minLevel), TrendingUp, {
            /* Somebody without the ladder (a moderator) keeps the level as it is. */
            onRemove: managesBadges ? () => remove('level', '') : undefined
          })}
        </ul>

        {/* F-151b: level 1 is every member (edge case of the proposal); with the gamification off a level opens nothing (decision 2). */}
        {chosen.minLevel?.level === 1 && (
          <p className="text-xs text-amber-600" data-level-first-warning>{t('common:access.levelFirstWarning')}</p>
        )}
        {chosen.minLevel && gamificationOn === false && (
          <p className="text-xs text-amber-600" data-level-gamification-off>{t('common:access.levelGamificationOff')}</p>
        )}

        {ownCount > 1 && (
          <p className="text-xs text-gray-500">{t('common:access.anyOfThese')}</p>
        )}

        {ownCount === 0 && productCount === 0 ? (
          inheritedCount > 0
            ? <p className="text-xs text-amber-600">{t('common:access.emptyInheritsSection')}</p>
            /* F-161a 7b: the same "only the team sees it" as the menu and the page. */
            : <AdminOnlyNotice variant="inline" />
        ) : (
          /* Said once something is chosen, because that is when the list looks
             complete and is not: administration is always on it, and somebody
             who reads the chips as the whole audience will wonder later how an
             admin got in. */
          <p className="text-xs text-gray-500">{t('common:access.plusStaff')}</p>
        )}
      </div>

      {productCount > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">{t('common:access.fromProducts')}</p>
          <ul className="space-y-1.5">
            {fromProducts!.map((product) => chip(`fp-${product.id}`, product.title, Package))}
          </ul>
        </div>
      )}
    </div>
  )
}
